import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { FORMA_DE_PAGAMENTO_LABELS, nomeDoMes, type FormaDePagamento } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { reais, type ExtratoDoCliente } from "@/lib/dinheiro";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { RegistrarRecebimento } from "./RegistrarRecebimento";

type Linha = ExtratoDoCliente["linhas"][number];
const data = (t: string | null) => (t ? t.split("-").reverse().join("/") : "—");

/** O extrato do cliente no mês, no formato que vai para o dentista, e os recebimentos. */
export function ExtratoDoClienteDialog({ alvo, onFechar }: { alvo: { clienteId: string; competencia: string } | null; onFechar: () => void }) {
  const podeLancar = usePermissao("finance.manage");
  const e = trpc.financeiro.aReceber.extrato.useQuery(alvo ?? { clienteId: "", competencia: "2026-01" }, { enabled: !!alvo }).data;

  return (
    <Dialog open={!!alvo} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[90dvh] max-w-4xl flex-col gap-4 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_FINANCEIRO.extratoDoCliente}>{e?.cliente.nome ?? "Extrato"}</DialogTitle>
          <DialogDescription>Extrato de {alvo ? nomeDoMes(alvo.competencia) : ""}: serviços concluídos e retrabalhos cobrados no mês.</DialogDescription>
        </DialogHeader>

        {!e ? (
          <p className="py-10 text-center text-sm text-zinc-500">Carregando…</p>
        ) : (
          <>
            <div className="flex justify-end">
              <BotaoExportar
                nome={`extrato-${e.cliente.nome.toLowerCase().replace(/\s+/g, "-")}-${e.competencia}`}
                titulo="extrato"
                tela="extrato-do-cliente"
                planilha="extrato"
                quantidade={e.linhas.length}
                idDe={(l: Linha) => l.id}
                buscarLinhas={async () => e.linhas}
                colunas={[
                  { titulo: "Data", valor: (l) => data(l.data) },
                  { titulo: "Pedido", valor: (l) => l.pedido },
                  { titulo: "Paciente", valor: (l) => l.paciente },
                  { titulo: "Dentista", valor: (l) => l.dentista },
                  { titulo: "Serviço", valor: (l) => l.servico },
                  { titulo: "Material", valor: (l) => l.material },
                  { titulo: "Dentes", valor: (l) => l.dentes },
                  { titulo: "Valor", valor: (l) => (l.semPreco ? null : l.valorCents / 100) },
                ]}
              />
            </div>
            <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-border">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-zinc-50/60 text-[10.5px] uppercase tracking-wider text-zinc-500 dark:bg-muted/30 dark:text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Data</th>
                    <th className="px-2 py-2 text-left font-semibold">Pedido</th>
                    <th className="px-2 py-2 text-left font-semibold">Paciente</th>
                    <th className="px-2 py-2 text-left font-semibold">Serviço</th>
                    <th className="px-2 py-2 text-left font-semibold">Dentes</th>
                    <th className="px-3 py-2 text-right font-semibold">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {e.linhas.map((l) => (
                    <tr key={l.id} className="border-t border-zinc-100 dark:border-border/50">
                      <td className="px-3 py-2 tabular-nums text-zinc-600 dark:text-foreground/70">{data(l.data)}</td>
                      <td className="px-2 py-2 tabular-nums text-zinc-600 dark:text-foreground/70">{l.pedido ? `#${l.pedido}` : "—"}</td>
                      <td className="px-2 py-2 text-zinc-900 dark:text-foreground">
                        {l.paciente ?? "—"}
                        {l.dentista && <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">{l.dentista}</span>}
                      </td>
                      <td className="px-2 py-2 text-zinc-900 dark:text-foreground">
                        {l.servico}
                        {l.material && <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">{l.material}</span>}
                      </td>
                      <td className="px-2 py-2 text-zinc-600 dark:text-foreground/70">{l.dentes ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-zinc-900 dark:text-foreground">
                        {l.semPreco ? <span className="text-xs font-semibold text-amber-600">Sem preço</span> : reais(l.valorCents)}
                      </td>
                    </tr>
                  ))}
                  {e.linhas.some((l) => l.semPreco) && (
                    <tr className="border-t border-zinc-100 dark:border-border/50">
                      <td colSpan={6} className="bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                        Serviço concluído sem preço não entra no total. Confira a tabela de preços deste cliente no catálogo.
                      </td>
                    </tr>
                  )}
                  {e.linhas.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-3 py-6 text-center text-zinc-500">Nada concluído neste mês.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 rounded-lg border border-zinc-200 p-3 text-sm dark:border-border">
                <p className="flex justify-between">
                  <span className="text-zinc-500 dark:text-muted-foreground">Total do mês</span>
                  <strong className="tabular-nums">{reais(e.faturadoCents)}</strong>
                </p>
                {e.recebimentos.map((r) => (
                  <p key={r.id} className="flex justify-between text-emerald-700 dark:text-emerald-400">
                    <span>
                      Recebido em {data(r.pagoEm)}
                      {r.formaDePagamento && ` · ${FORMA_DE_PAGAMENTO_LABELS[r.formaDePagamento as FormaDePagamento]}`}
                    </span>
                    <span className="tabular-nums">− {reais(r.valorCents)}</span>
                  </p>
                ))}
                <p className="flex justify-between border-t border-zinc-100 pt-1.5 dark:border-border">
                  <span className="font-semibold">{e.saldoCents < 0 ? "Pago a mais" : "Saldo a receber"}</span>
                  <strong className={e.saldoCents > 0 ? "tabular-nums text-red-600" : "tabular-nums text-emerald-600"}>{reais(Math.abs(e.saldoCents))}</strong>
                </p>
              </div>
              {podeLancar && e.saldoCents > 0 && <RegistrarRecebimento clienteId={e.cliente.id} competencia={e.competencia} saldoCents={e.saldoCents} />}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
